import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { Menu } from '@headlessui/react';
import {
    UserIcon,
    PencilIcon,
    XMarkIcon,
    CheckIcon,
    CurrencyRupeeIcon,
    ClockIcon,
    DocumentTextIcon,
    ChartBarIcon,
    IdentificationIcon,
    EnvelopeIcon,
    PhoneIcon,
    HomeIcon,
    ArrowPathIcon,
    LinkIcon,
    CreditCardIcon,
    ShieldCheckIcon,
    UserGroupIcon,
    ChartPieIcon,
    EllipsisVerticalIcon,
    FunnelIcon,
    ChevronDownIcon,
    CalendarIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    XCircleIcon,
    BanknotesIcon,
    WalletIcon
} from '@heroicons/react/24/outline';
import { MdAddCircleOutline } from "react-icons/md";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDeviceInfo } from '../utils/deviceInfo';
import { motion, AnimatePresence } from 'framer-motion';
import { Switch } from '@headlessui/react';

const API_URL = process.env.REACT_APP_API_URL;

const UserProfile = () => {
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('userId');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editFormData, setEditFormData] = useState({
        name: '',
        email: '',
        mobile: '',
        address: '',
        callbackurl: '',
        payment_callback: '',
        api_log: '',
        api_ip: '',
        refer_code: '',
        plan_id: '',
        status: '',
        otp_code: '',
        otp_try: '',
        otp_daily_limit: '',
        dist_user_id: ''
    });
    const [addMoneyModalOpen, setAddMoneyModalOpen] = useState(false);
    const [addMoneyForm, setAddMoneyForm] = useState({
        category: '1',
        amount: '',
        transactionId: '',
        provider: ''
    });
    const [providers, setProviders] = useState([]);
    const [processing, setProcessing] = useState(false);
    const navigate = useNavigate();
    const [access_data, setAccess_data] = useState([]);
    const [kycModalOpen, setKycModalOpen] = useState(false);
    const [kycData, setKycData] = useState([]);
    const [kycLoading, setKycLoading] = useState(false);

    useEffect(() => {
        if (sessionCheck() == null) {
            navigate("../login", { replace: true });
        } else {
            setAccess_data(JSON.parse(sessionCheck()));
        }
    }, []);

    useEffect(() => {
        if (access_data?.username && userId) {
            fetchUserData();
        }
    }, [access_data?.username, userId]);

    const fetchUserData = async () => {
        setLoading(true);
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'user-profile',
                    user_id: userId
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            const response = await fetch(`${API_URL}user-profile.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (result.status === true) {
                setUserData(result.data);
                setEditFormData({
                    name: result.data.name || '',
                    email: result.data.email || '',
                    mobile: result.data.mobile || '',
                    address: result.data.address || '',
                    callbackurl: result.data.callbackurl || '',
                    payment_callback: result.data.payment_callback || '',
                    api_log: result.data.api_log || '',
                    api_ip: result.data.api_ip || '',
                    refer_code: result.data.refer_code || '',
                    plan_id: result.data.plan_id || '',
                    status: result.data.status || '',
                    otp_code: result.data.otp_code || '',
                    otp_try: result.data.otp_try || '',
                    otp_daily_limit: result.data.otp_daily_limit || '',
                    dist_user_id: result.data.dist_user_id || ''
                });
            } else {
                toast.error(result.message || "Failed to fetch user data");
            }
        } catch (error) {
            console.error("Error fetching user data:", error);
            toast.error("An error occurred while fetching user data");
        } finally {
            setLoading(false);
        }
    };

    const fetchPaymentProviders = async () => {
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'fetch-provider',
                    category: '3'
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            const response = await fetch(`${API_URL}provider-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (result.status === true) {
                setProviders(result.data);
            } else {
                toast.error(result.message || "Failed to fetch payment providers");
            }
        } catch (error) {
            console.error("Error fetching payment providers:", error);
            toast.error("An error occurred while fetching payment providers");
        }
    };

    useEffect(() => {
        if (addMoneyModalOpen) {
            fetchPaymentProviders();
        }
    }, [addMoneyModalOpen]);

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddMoneyChange = (e) => {
        const { name, value } = e.target;
        setAddMoneyForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleStatusToggle = (value) => {
        setEditFormData(prev => ({
            ...prev,
            status: value ? '1' : '0'
        }));
    };

    const handleOtpVerifiedToggle = (value) => {
        setEditFormData(prev => ({
            ...prev,
            otp_code: value ? '1' : '0'
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'update-user-profile',
                    user_id: userId,
                    updates: editFormData
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            const response = await fetch(`${API_URL}update-user-profile.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (result.status === true) {
                toast.success("Profile updated successfully");
                setUserData(prev => ({ ...prev, ...editFormData }));
                setEditModalOpen(false);
            } else {
                toast.error(result.message || "Failed to update profile");
            }
        } catch (error) {
            console.error("Error updating profile:", error);
            toast.error("An error occurred while updating profile");
        }
    };

    const handleAddMoneySubmit = async (e) => {
        e.preventDefault();
        setProcessing(true);

        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'manual-addmoney',
                    user_id: userId,
                    category: addMoneyForm.category,
                    amount: addMoneyForm.amount,
                    transaction_id: addMoneyForm.transactionId,
                    provider: addMoneyForm.provider
                }
            };

            console.log(dataToEncrypt);

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            const response = await fetch(`${API_URL}topup-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (result.status === true) {
                toast.success("Money added successfully");
                setProcessing(false);
                setAddMoneyModalOpen(false);
                setAddMoneyForm({
                    category: '',
                    amount: '',
                    transactionId: '',
                    provider: ''
                });
                fetchUserData();
            } else {
                throw new Error(result.message || "Failed to add money");
            }
        } catch (error) {
            toast.info(error.message || "Add money is pending from API end, cliently check after sometime.");
            setProcessing(false);
            setAddMoneyModalOpen(false);
            setAddMoneyForm({
                category: '',
                amount: '',
                transactionId: '',
                provider: ''
            });
            fetchUserData();
        }
    };

    const toggleMobileSidebar = () => {
        setMobileSidebarOpen(!mobileSidebarOpen);
    };

    const fetchKYCData = async () => {
        setKycLoading(true);
        setKycModalOpen(true);
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'fetch-kyc',
                    user_id: userId,
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            const response = await fetch(`${API_URL}kyc-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (result.status === true) {
                setKycData(result.data);
            } else {
                toast.error(result.message || "Failed to fetch kyc profile");
            }
        } catch (error) {
            console.error("Error fetching kyc profile:", error);
            toast.error("An error occurred while fetching kyc profile");
        } finally {
            setKycLoading(false);
        }
    };

    // Skeleton Loader Component
    const SkeletonLoader = () => (
        <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white flex">
            <div className={`flex flex-col flex-1 transition-all duration-300 ease-in-out md:ml-20`}>
                <Header toggleMobileSidebar={toggleMobileSidebar} />
                <main className="flex-1 p-6 overflow-auto">
                    <div className="max-w-7xl mx-auto">
                        {/* Profile Header Skeleton */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden mb-6 animate-pulse">
                            <div className="md:flex">
                                <div className="md:w-1/4 bg-gray-100 dark:bg-gray-700 p-6 flex flex-col items-center">
                                    <div className="h-32 w-32 rounded-full bg-gray-300 dark:bg-gray-600"></div>
                                    <div className="h-6 w-3/4 mt-4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                    <div className="h-4 w-1/2 mt-2 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                    <div className="h-5 w-20 mt-2 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                                </div>
                                <div className="md:w-3/4 p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-4">
                                            <div className="h-6 w-1/3 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                            {[...Array(4)].map((_, i) => (
                                                <div key={i} className="space-y-2">
                                                    <div className="h-4 w-1/4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                                    <div className="h-5 w-3/4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="space-y-4">
                                            <div className="h-6 w-1/3 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                            {[...Array(4)].map((_, i) => (
                                                <div key={i} className="space-y-2">
                                                    <div className="h-4 w-1/4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                                    <div className="h-5 w-3/4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons Skeleton */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            {[...Array(4)].map((_, i) => (
                                <div key={i} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow h-16 bg-gray-300 dark:bg-gray-600 animate-pulse"></div>
                            ))}
                        </div>

                        {/* Additional Details Skeleton */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden animate-pulse">
                            <div className="p-6">
                                <div className="h-6 w-1/4 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <div className="h-5 w-1/3 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                                        {[...Array(4)].map((_, i) => (
                                            <div key={i} className="space-y-2 mt-3">
                                                <div className="h-4 w-1/4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                                <div className="h-5 w-full bg-gray-300 dark:bg-gray-600 rounded"></div>
                                            </div>
                                        ))}
                                    </div>
                                    <div>
                                        <div className="h-5 w-1/3 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                                        {[...Array(4)].map((_, i) => (
                                            <div key={i} className="space-y-2 mt-3">
                                                <div className="h-4 w-1/4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                                                <div className="h-5 w-full bg-gray-300 dark:bg-gray-600 rounded"></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );

    if (loading) {
        return <SkeletonLoader />;
    }

    if (!userData) {
        return (
            <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white flex">
                <div className={`flex flex-col flex-1 transition-all duration-300 ease-in-out md:ml-20`}>
                    <Header toggleMobileSidebar={toggleMobileSidebar} />
                    <div className="flex justify-center items-center h-full">
                        <p className="text-gray-600 dark:text-gray-300">User not found</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
            <div className="flex flex-col flex-1">
                <Header />
                <main className="flex-1 p-6 overflow-auto">
                    <div className="max-w-7xl mx-auto">
                        {/* Profile Header */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden mb-6">
                            <div className="md:flex">
                                <div className="md:w-1/4 bg-gray-100 dark:bg-gray-700 p-6 flex flex-col items-center">
                                    <div className="relative">
                                        <div className="h-32 w-32 rounded-full bg-blue-500 flex items-center justify-center text-white text-4xl font-bold">
                                            {userData.name?.charAt(0) || 'U'}
                                        </div>
                                        <button
                                            onClick={() => setEditModalOpen(true)}
                                            className="absolute bottom-0 right-0 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 transition dark:bg-gray-600 dark:hover:bg-gray-500"
                                        >
                                            <PencilIcon className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                                        </button>
                                    </div>
                                    <h1 className="mt-4 text-2xl font-bold text-gray-800 dark:text-white">{userData.name}</h1>
                                    <p className="text-gray-600 dark:text-gray-300">User ID: {userData.user_id}</p>
                                    <div className={`mt-2 px-3 py-1 rounded-full text-sm font-medium ${userData.status === '1' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                        }`}>
                                        {userData.status === '1' ? 'Active' : 'Inactive'}
                                    </div>
                                </div>

                                <div className="md:w-3/4 p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Basic Info */}
                                        <div className="space-y-4">
                                            <h2 className="text-xl font-semibold text-gray-800 dark:text-white border-b pb-2">Basic Information</h2>
                                            <div className="flex items-start">
                                                <EnvelopeIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                                                    <p className="text-gray-800 dark:text-gray-200">{userData.email || 'Not provided'}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <PhoneIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Mobile</p>
                                                    <p className="text-gray-800 dark:text-gray-200">{userData.mobile || 'Not provided'}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <HomeIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Address</p>
                                                    <p className="text-gray-800 dark:text-gray-200">{userData.address || 'Not provided'}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <UserGroupIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Referral By</p>
                                                    <p className="text-gray-800 dark:text-gray-200">{userData.refer_by || 'Not referred'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Account Info */}
                                        <div className="space-y-4">
                                            <h2 className="text-xl font-semibold text-gray-800 dark:text-white border-b pb-2">Account Information</h2>
                                            <div className="flex items-start">
                                                <CurrencyRupeeIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Balance</p>
                                                    <p className="text-gray-800 dark:text-gray-200">₹{parseFloat(userData.balance || 0).toFixed(2)}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <ClockIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Joined On</p>
                                                    <p className="text-gray-800 dark:text-gray-200">{new Date(userData.dor).toLocaleDateString()}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <IdentificationIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Referral Code</p>
                                                    <p className="text-gray-800 dark:text-gray-200">{userData.refer_code || 'Not available'}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <CreditCardIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-1 mr-2" />
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Plan ID</p>
                                                    <p className="text-gray-800 dark:text-gray-200">{userData.plan_id || 'Not assigned'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                            <button
                                onClick={() => setAddMoneyModalOpen(true)}
                                className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow hover:shadow-md transition flex items-center justify-center space-x-2"
                            >
                                <BanknotesIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                                <span className="dark:text-gray-200">Add Money</span>
                            </button>
                            <button onClick={() => navigate(`/order-info?userId=${userId}`)} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow hover:shadow-md transition flex items-center justify-center space-x-2">
                                <DocumentTextIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                                <span className="dark:text-gray-200">View Transactions</span>
                            </button>
                            <button onClick={() => navigate(`/user-report?userId=${userId}`)} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow hover:shadow-md transition flex items-center justify-center space-x-2">
                                <ChartBarIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                                <span className="dark:text-gray-200">View Reports</span>
                            </button>
                            <button onClick={() => navigate(`/ledger?userId=${userId}`)} className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow hover:shadow-md transition flex items-center justify-center space-x-2">
                                <CurrencyRupeeIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                                <span className="dark:text-gray-200">View Ledger</span>
                            </button>
                            <button
                                onClick={fetchUserData}
                                className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow hover:shadow-md transition flex items-center justify-center space-x-2"
                            >
                                <ArrowPathIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                                <span className="dark:text-gray-200">Refresh Data</span>
                            </button>
                        </div>

                        {/* Additional Details */}
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
                            <div className="p-6">
                                <h2 className="text-xl font-semibold text-gray-800 dark:text-white border-b pb-2 mb-4">Additional Details</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-2">API Information</h3>
                                        <div className="space-y-3">
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">API Log</p>
                                                <p className="text-gray-800 dark:text-gray-200">{userData.api_log || 'Not available'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">API IP</p>
                                                <p className="text-gray-800 dark:text-gray-200">{userData.api_ip || 'Not available'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Callback URL</p>
                                                <p className="text-gray-800 dark:text-gray-200 break-all">{userData.callbackurl || 'Not set'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Payment Callback</p>
                                                <p className="text-gray-800 dark:text-gray-200 break-all">{userData.payment_callback || 'Not set'}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Verification & Security</h3>
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">eKYC Status</p>
                                                    <p
                                                        className={`inline-flex px-2 text-xs font-semibold rounded-full ${userData.iskyc === 'Yes'
                                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                            }`}
                                                    >
                                                        {userData.iskyc === 'Yes' ? 'Yes' : 'No'}
                                                    </p>
                                                </div>


                                                <button
                                                    onClick={fetchKYCData}
                                                    className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
                                                >
                                                    View KYC Data
                                                </button>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">OTP Attempts</p>
                                                <p className="text-gray-800 dark:text-gray-200">{userData.otp_try || '0'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Daily OTP Limit</p>
                                                <p className="text-gray-800 dark:text-gray-200">{userData.otp_daily_limit || '0'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Distributor ID</p>
                                                <p className="text-gray-800 dark:text-gray-200">{userData.dist_user_id || 'Not assigned'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {/* Edit Modal */}
            <AnimatePresence>
                {editModalOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
                        onClick={() => setEditModalOpen(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Fixed Header */}
                            <div className="flex justify-between items-center border-b dark:border-gray-700 p-4 sticky top-0 bg-white dark:bg-gray-800 z-10">
                                <h3 className="text-lg font-semibold dark:text-white">Edit Profile</h3>
                                <button
                                    onClick={() => setEditModalOpen(false)}
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            {/* Scrollable Content */}
                            <div className="overflow-y-auto flex-1 p-4">
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Basic Info */}
                                        <div className="space-y-4">
                                            <h4 className="font-medium text-gray-700 dark:text-gray-300">Basic Information</h4>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name*</label>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={editFormData.name}
                                                    onChange={handleEditChange}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                    required
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={editFormData.email}
                                                    onChange={handleEditChange}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mobile*</label>
                                                <input
                                                    type="tel"
                                                    name="mobile"
                                                    value={editFormData.mobile}
                                                    onChange={handleEditChange}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                    required
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address</label>
                                                <textarea
                                                    name="address"
                                                    value={editFormData.address}
                                                    onChange={handleEditChange}
                                                    rows="3"
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                ></textarea>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Referral Code</label>
                                                <input
                                                    type="text"
                                                    name="refer_code"
                                                    value={editFormData.refer_code}
                                                    onChange={handleEditChange}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                />
                                            </div>
                                        </div>

                                        {/* Account Info */}
                                        <div className="space-y-4">
                                            <h4 className="font-medium text-gray-700 dark:text-gray-300">Account Information</h4>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Plan ID</label>
                                                <input
                                                    type="text"
                                                    name="plan_id"
                                                    value={editFormData.plan_id}
                                                    onChange={handleEditChange}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                                                <Switch
                                                    checked={editFormData.status === '1'}
                                                    onChange={handleStatusToggle}
                                                    className={`${editFormData.status === '1' ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                                                        } relative inline-flex h-6 w-11 items-center rounded-full`}
                                                >
                                                    <span className="sr-only">Enable status</span>
                                                    <span
                                                        className={`${editFormData.status === '1' ? 'translate-x-6' : 'translate-x-1'
                                                            } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                                                    />
                                                </Switch>
                                                <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">
                                                    {editFormData.status === '1' ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">API Log</label>
                                                <input
                                                    type="text"
                                                    name="api_log"
                                                    value={editFormData.api_log}
                                                    onChange={handleEditChange}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">API IP</label>
                                                <input
                                                    type="text"
                                                    name="api_ip"
                                                    value={editFormData.api_ip}
                                                    onChange={handleEditChange}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Distributor ID</label>
                                                <input
                                                    type="text"
                                                    name="dist_user_id"
                                                    value={editFormData.dist_user_id}
                                                    onChange={handleEditChange}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* API Info */}
                                        <div className="space-y-4">
                                            <h4 className="font-medium text-gray-700 dark:text-gray-300">API Information</h4>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Callback URL</label>
                                                <input
                                                    type="url"
                                                    name="callbackurl"
                                                    value={editFormData.callbackurl}
                                                    onChange={handleEditChange}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Callback</label>
                                                <input
                                                    type="url"
                                                    name="payment_callback"
                                                    value={editFormData.payment_callback}
                                                    onChange={handleEditChange}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                />
                                            </div>
                                        </div>

                                        {/* Verification */}
                                        <div className="space-y-4">
                                            <h4 className="font-medium text-gray-700 dark:text-gray-300">Verification & Security</h4>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">eKYC Verified</label>
                                                <Switch
                                                    checked={editFormData.otp_code === '1'}
                                                    onChange={handleOtpVerifiedToggle}
                                                    className={`${editFormData.otp_code === '1' ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                                                        } relative inline-flex h-6 w-11 items-center rounded-full`}
                                                >
                                                    <span className="sr-only">Enable verification</span>
                                                    <span
                                                        className={`${editFormData.otp_code === '1' ? 'translate-x-6' : 'translate-x-1'
                                                            } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                                                    />
                                                </Switch>
                                                <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">
                                                    {editFormData.otp_code === '1' ? 'Verified' : 'Not Verified'}
                                                </span>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">OTP Attempts</label>
                                                <input
                                                    type="number"
                                                    name="otp_try"
                                                    value={editFormData.otp_try}
                                                    onChange={handleEditChange}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Daily OTP Limit</label>
                                                <input
                                                    type="number"
                                                    name="otp_daily_limit"
                                                    value={editFormData.otp_daily_limit}
                                                    onChange={handleEditChange}
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            {/* Fixed Footer */}
                            <div className="flex justify-end space-x-3 p-4 border-t dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800">
                                <button
                                    type="button"
                                    onClick={() => setEditModalOpen(false)}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
                                    onClick={handleSubmit}
                                >
                                    <CheckIcon className="h-5 w-5" />
                                    <span>Save Changes</span>
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Add Money Modal */}
            <AnimatePresence>
                {addMoneyModalOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
                        onClick={() => !processing && setAddMoneyModalOpen(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Fixed Header */}
                            <div className="flex justify-between items-center border-b dark:border-gray-700 p-4 sticky top-0 bg-white dark:bg-gray-800 z-10">
                                <h3 className="text-lg font-semibold dark:text-white">Add Money</h3>
                                <button
                                    onClick={() => !processing && setAddMoneyModalOpen(false)}
                                    disabled={processing}
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            {/* Scrollable Content */}
                            <div className="overflow-y-auto flex-1 p-4">
                                <form onSubmit={handleAddMoneySubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Category*
                                        </label>
                                        <select
                                            name="category"
                                            value={addMoneyForm.category}
                                            onChange={handleAddMoneyChange}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                            required
                                        >
                                            <option value="">Select provider</option>
                                            <option value="1">Add Money</option>
                                            <option value="4">Bonus</option>
                                            <option value="5">Debit Money</option>
                                            <option value="8">Debit Adjustment</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Amount (₹)*
                                        </label>
                                        <input
                                            type="number"
                                            name="amount"
                                            value={addMoneyForm.amount}
                                            onChange={handleAddMoneyChange}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                            placeholder="Enter amount"
                                            required
                                            min="1"
                                            step="0.01"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Transaction ID*
                                        </label>
                                        <input
                                            type="text"
                                            name="transactionId"
                                            value={addMoneyForm.transactionId}
                                            onChange={handleAddMoneyChange}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                            placeholder="Enter transaction ID"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Payment Provider*
                                        </label>
                                        <select
                                            name="provider"
                                            value={addMoneyForm.provider}
                                            onChange={handleAddMoneyChange}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                            required
                                            disabled={providers.length === 0}
                                        >
                                            <option value="">Select provider</option>
                                            {providers.map((provider) => (
                                                <option key={provider.company_id} value={provider.company_id}>
                                                    {provider.company_name}
                                                </option>
                                            ))}
                                        </select>
                                        {providers.length === 0 && (
                                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                                Loading providers...
                                            </p>
                                        )}
                                    </div>
                                </form>
                            </div>

                            {/* Fixed Footer */}
                            <div className="flex justify-end space-x-3 p-4 border-t dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800">
                                <button
                                    type="button"
                                    onClick={() => !processing && setAddMoneyModalOpen(false)}
                                    disabled={processing}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleAddMoneySubmit}
                                    disabled={processing}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2 disabled:opacity-50"
                                >
                                    {processing ? (
                                        <>
                                            <ArrowPathIcon className="h-5 w-5 animate-spin" />
                                            <span>Processing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <BanknotesIcon className="h-5 w-5" />
                                            <span>Add Money</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* KYC Data Modal */}
            <AnimatePresence>
                {kycModalOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
                        onClick={() => !kycLoading && setKycModalOpen(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Fixed Header */}
                            <div className="flex justify-between items-center border-b dark:border-gray-700 p-4 sticky top-0 bg-white dark:bg-gray-800 z-10">
                                <h3 className="text-lg font-semibold dark:text-white">KYC Details</h3>
                                <button
                                    onClick={() => !kycLoading && setKycModalOpen(false)}
                                    disabled={kycLoading}
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            {/* Scrollable Content */}
                            <div className="overflow-y-auto flex-1 p-4">
                                {kycLoading ? (
                                    <div className="flex justify-center items-center h-64">
                                        <ArrowPathIcon className="h-12 w-12 text-blue-500 animate-spin" />
                                    </div>
                                ) : kycData.length > 0 ? (
                                    <div className="space-y-6">
                                        {/* Sort KYC data with status=1 first */}
                                        {[...kycData]
                                            .sort((a, b) => b.status - a.status)
                                            .map((kyc, index) => (
                                                <div
                                                    key={index}
                                                    className={`border rounded-lg p-4 ${kyc.status === "1" ? 'border-green-200 bg-green-50 dark:bg-green-900 dark:border-green-700' : 'border-gray-200 bg-gray-50 dark:bg-gray-700 dark:border-gray-600'}`}
                                                >
                                                    <div className="flex justify-between items-start mb-2">
                                                        <h4 className="font-medium text-gray-700 dark:text-gray-200">
                                                            KYC Attempt #{kyc.sl}
                                                        </h4>
                                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${kyc.status === "1" ? 'bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-100' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-700 dark:text-yellow-100'}`}>
                                                            {kyc.status === "1" ? 'Verified' : 'Pending'}
                                                        </span>
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div className="space-y-2">
                                                            <div>
                                                                <p className="text-sm text-gray-500 dark:text-gray-400">Aadhaar Number</p>
                                                                <p className="text-gray-800 dark:text-gray-200">{kyc.aadhaar || 'Not provided'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500 dark:text-gray-400">Name</p>
                                                                <p className="text-gray-800 dark:text-gray-200">{kyc.aadhaar_name || 'Not provided'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500 dark:text-gray-400">Father's Name</p>
                                                                <p className="text-gray-800 dark:text-gray-200">{kyc.aadhaar_father || 'Not provided'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500 dark:text-gray-400">Date of Birth</p>
                                                                <p className="text-gray-800 dark:text-gray-200">{kyc.aadhaar_dob || 'Not provided'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500 dark:text-gray-400">Gender</p>
                                                                <p className="text-gray-800 dark:text-gray-200">{kyc.aadhaar_gender || 'Not provided'}</p>
                                                            </div>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <div>
                                                                <p className="text-sm text-gray-500 dark:text-gray-400">State</p>
                                                                <p className="text-gray-800 dark:text-gray-200">{kyc.aadhaar_state || 'Not provided'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500 dark:text-gray-400">District</p>
                                                                <p className="text-gray-800 dark:text-gray-200">{kyc.aadhaar_district || 'Not provided'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500 dark:text-gray-400">Address</p>
                                                                <p className="text-gray-800 dark:text-gray-200">{kyc.address || 'Not provided'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500 dark:text-gray-400">PIN Code</p>
                                                                <p className="text-gray-800 dark:text-gray-200">{kyc.aadhaar_pin || 'Not provided'}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500 dark:text-gray-400">Mobile</p>
                                                                <p className="text-gray-800 dark:text-gray-200">{kyc.aadhaar_mobile || 'Not provided'}</p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {kyc.profile_image && (
                                                        <div className="mt-4">
                                                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Profile Image</p>
                                                            <img
                                                                src={`data:image/jpeg;base64,${kyc.profile_image}`}
                                                                alt="Profile"
                                                                className="h-32 w-32 rounded object-cover border border-gray-200 dark:border-gray-600"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                    </div>
                                ) : (
                                    <div className="flex justify-center items-center h-64">
                                        <p className="text-gray-500 dark:text-gray-400">No KYC data available</p>
                                    </div>
                                )}
                            </div>

                            {/* Fixed Footer */}
                            <div className="flex justify-end p-4 border-t dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800">
                                <button
                                    type="button"
                                    onClick={() => setKycModalOpen(false)}
                                    disabled={kycLoading}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                    Close
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <ToastContainer />
        </div>
    );
};

export default UserProfile;